import React, { useEffect, useReducer, useState } from 'react';
import { Icon } from '@sabre/spark-react-core';
import Accordion from '@sabre/spark-react-core/accordion';
import { Drawer } from '@sabre/spark-react-core/accordion/drawer';
import Button from '@sabre/spark-react-core/button';
import { cloneDeep } from 'lodash';
import { object, func, bool } from 'prop-types';
import {
   FORM_DATA_ACTION_TYPE,
   SIZE,
   NON_ACCORDION_CATEGORY,
   PRODUCT_TYPE,
} from '../../../utils/constants.js';
import { NOTIFICATION_MESSAGES } from '../../../utils/notification-messages.js';
import {
   replaceKeyAndRemoveField,
   updateSelectedCheckboxes,
   getUpdatedConfig,
   generateConstructedPayload,
   toggleWinningFareCheckBox,
   findConstructedAndSpecifiedStatus,
   generateSpecifiedPayload,
   initialState,
   getCheckBoxSelectionDetails,
   INPUT_TYPE,
   removeEmptyFields,
   validateReceivedDateFormat,
} from './__helpers__/functions.js';
import formDataReducer from './form-data-reducer.js';
import Field from './search-criteria-field.jsx';

const SearchCriteriaComponent = ({
   searchCriteriaData,
   onSubmit,
   onError,
   onSaveSearchCriteria,
   onStopSearchRequest,
   selectedSearchCriteria,
   isExistingCriteria,
}) => {
   const [formData, dispatchFormData] = useReducer(
      formDataReducer,
      initialState()
   );
   const [isMinimized, setIsMinimized] = useState(false);
   const [searchCriteriaConfig, setSearchCriteriaConfig] = useState([]);
   const [selectedCheckboxes, setSelectedCheckboxes] = useState(new Set());

   const ACCORDION_SCREENS = ['RULES'];

   useEffect(() => {
      const defaultConfig = searchCriteriaData.map((item) => ({
         ...item,
         config: item.config.filter(
            (section) =>
               !section.isConfinedTo || section.isConfinedTo.length === 0
         ),
      }));

      setSearchCriteriaConfig(defaultConfig);
   }, [searchCriteriaData]);

   useEffect(() => {
      const removedPayload = replaceKeyAndRemoveField(cloneDeep(formData));
      const savePayload = generateConstructedPayload(removedPayload);

      if (onSaveSearchCriteria) onSaveSearchCriteria(savePayload);
   }, [formData]);

   useEffect(() => {
      if (selectedSearchCriteria && isExistingCriteria) {
         dispatchFormData({ type: FORM_DATA_ACTION_TYPE.RESET_FORM });
         for (const [sectionKey, searchData] of Object.entries(
            selectedSearchCriteria
         )) {
            if (Array.isArray(searchData)) {
               searchData?.forEach((fieldDetails) => {
                  searchCriteriaData[0].config.forEach((details) => {
                     if (details.sectionKey === sectionKey) {
                        details.fields.forEach((inputDetails) => {
                           if (inputDetails.field === fieldDetails.key) {
                              dispatchFormData({
                                 type: FORM_DATA_ACTION_TYPE.ADD_FIELD,
                                 payload: {
                                    sectionKey,
                                    field: fieldDetails.key,
                                    operator: fieldDetails.operator,
                                    value: fieldDetails.value,
                                    key: inputDetails.key,
                                 },
                              });
                           }
                        });
                     }
                  });
               });
            } else if (['productType', 'table'].includes(sectionKey)) {
               const checkedFieldCode =
                  sectionKey === 'productType' ? searchData : 'W';
               getCheckBoxSelectionDetails(checkedFieldCode).forEach(
                  (fieldDetails) => {
                     dispatchFormData({
                        type: FORM_DATA_ACTION_TYPE.ADD_FIELD,
                        payload: {
                           sectionKey,
                           field: fieldDetails.field,
                           operator: fieldDetails.operator,
                           value: fieldDetails.value,
                           key: fieldDetails.key,
                        },
                     });
                  }
               );
            }
         }
      }
   }, [searchCriteriaData, selectedSearchCriteria, isExistingCriteria]);

   const handleMinimize = () => {
      setIsMinimized((prev) => !prev);
   };

   const handleSubmit = async (event) => {
      event.preventDefault();
      const removedPayload = replaceKeyAndRemoveField(cloneDeep(formData));
      const { isConstructedFare, isSpecifiedFare } =
         findConstructedAndSpecifiedStatus(removedPayload);
      const updatedPayload = removeEmptyFields(removedPayload);
      validatePayload(updatedPayload, isConstructedFare, isSpecifiedFare);
      setIsMinimized(true);
   };

   const submitPayload = (payload, isConstructed, isSpecified) => {
      const constructedFaresPayload = generateConstructedPayload(payload);
      const specifiedFaresPayload = generateSpecifiedPayload(payload);
      onSubmit(
         constructedFaresPayload,
         specifiedFaresPayload,
         isConstructed,
         isSpecified
      );
   };

   const validatePayload = (payload, isConstructed, isSpecified) => {
      const isEmptyPayload =
         !payload ||
         (typeof payload === 'object' &&
            !Array.isArray(payload) &&
            Object.keys(payload).length === 0);

      if (isEmptyPayload) {
         onError(NOTIFICATION_MESSAGES.EMPTY_SEARCH_CRITERIA);
         return;
      }

      const productType = payload.productType || [];
      const identifiers = payload.identifiers || [];
      const markets = payload.markets || [];

      const search = payload.search || [];
      const distributions = payload.distributions || [];

      const identifierKeys = identifiers.map((item) => item.key);
      const marketKeys = markets.map((item) => item.key);
      const searchKeys = search.map((item) => item.key);

      const distributionsKeys = distributions.map((item) => item.key);

      const hasOrigin = identifierKeys.includes('origin');
      const hasDestination = identifierKeys.includes('destination');
      const hasCarrier = identifierKeys.includes('carrier_code');
      const hasTariff = identifierKeys.includes('tariffNumber');
      const hasOriginCountry = marketKeys.includes('originCountryCode');
      const hasDestinationCountry = marketKeys.includes(
         'destinationCountryCode'
      );
      const hasMarketId = searchKeys.includes('marketId');

      const hasReceivedDate = distributionsKeys.includes(
         'transmissionDateTime'
      );

      const isProductTypeSelected = productType.some(
         (item) =>
            (item.key === PRODUCT_TYPE.SPECIFIED && item.value === true) ||
            (item.key === PRODUCT_TYPE.CONSTRUCTED && item.value === true)
      );
      if (!isProductTypeSelected) {
         onError(NOTIFICATION_MESSAGES.INVALID_PRODUCT_TYPE);
         return;
      }
      const isConstructedSelected = productType.some(
         (item) => item.key === PRODUCT_TYPE.CONSTRUCTED && item.value === true
      );
      if (hasReceivedDate) {
         const receivedDate = distributions.find(
            (record) => record.key === 'transmissionDateTime'
         )?.value;
         const isValidFormat = validateReceivedDateFormat(receivedDate);
         if (!isValidFormat) {
            onError(
               `Received ${NOTIFICATION_MESSAGES.INVALID_DATE_TIME_FORMAT}`
            );
            return;
         }
      }

      if (isConstructedSelected) {
         if (!hasOrigin || !hasDestination || !hasCarrier) {
            onError(NOTIFICATION_MESSAGES.INVALID_CONSTRUCTED_FIELDS);
            return;
         } else {
            submitPayload(payload, isConstructed, isSpecified);
         }
      }

      if (!isConstructedSelected) {
         const hasValidSpecifiedCombination =
            hasTariff ||
            hasMarketId ||
            (hasOrigin && hasDestination) ||
            (hasOrigin && hasDestinationCountry) ||
            (hasDestination && hasOriginCountry) ||
            (hasOriginCountry && hasDestinationCountry);

         if (!hasValidSpecifiedCombination) {
            onError(NOTIFICATION_MESSAGES.INVALID_SPECIFIED_FIELDS);
            return;
         } else {
            submitPayload(payload, isConstructed, isSpecified);
         }
      }
   };

   const handleChange = ({
      key,
      value,
      field,
      operator,
      sectionKey,
      sectionForm,
   }) => {
      if (typeof value === 'boolean') {
         setSelectedCheckboxes((prevSelected) => {
            let newConfig = [];
            const updatedSelection = updateSelectedCheckboxes(
               prevSelected,
               key,
               value
            );
            newConfig = getUpdatedConfig(updatedSelection, searchCriteriaData);
            if (field === PRODUCT_TYPE.CONSTRUCTED) {
               newConfig = toggleWinningFareCheckBox(newConfig, value);
            }

            setSearchCriteriaConfig(newConfig);
            return updatedSelection;
         });
      }

      const isExistingField = formData[sectionKey]?.find(
         (field) => field.key === key
      );

      if (sectionForm === INPUT_TYPE.RADIO_BUTTON) {
         const isExistingField = formData[sectionKey]?.length > 0;

         dispatchFormData({
            type: isExistingField
               ? FORM_DATA_ACTION_TYPE.UPDATE_SECTION
               : FORM_DATA_ACTION_TYPE.ADD_FIELD,
            payload: {
               sectionKey,
               operator,
               field,
               key: value,
               value: true,
            },
         });

         return;
      }

      dispatchFormData({
         type: isExistingField
            ? FORM_DATA_ACTION_TYPE.UPDATE_FIELD
            : FORM_DATA_ACTION_TYPE.ADD_FIELD,
         payload: {
            sectionKey,
            operator,
            field,
            key,
            value,
         },
      });
   };

   const ToggleButton = () => (
      <button
         type='button'
         className='minimize-button'
         onClick={handleMinimize}
      >
         <Icon name={`video-control-${isMinimized ? 'forward' : 'rewind'}`} />
      </button>
   );

   const renderFields = (fields, sectionKey, sectionForm) => {
      return fields.map(({ key, field, ...fieldProps }, fieldIndex) => {
         const inputValue = formData[sectionKey]?.find((field) => {
            if (sectionForm === INPUT_TYPE.RADIO_BUTTON) {
               return field.key;
            }
            return field.key === key;
         });

         const paddingClass = fieldIndex % 2 === 0 ? 'pr-1' : 'pl-1';

         return (
            <Field
               key={`${key}-${fieldIndex}`}
               name={key}
               paddingClass={paddingClass}
               onChange={({ value, operator }) =>
                  handleChange({
                     key,
                     value,
                     field,
                     operator,
                     sectionKey,
                     sectionForm,
                  })
               }
               inputValue={inputValue}
               {...fieldProps}
            />
         );
      });
   };

   const RenderAccordion = (config, isAccordion) => {
      return config.map(
         ({ sectionName, sectionKey, fields, sectionForm }, index) => {
            const shouldBypassAccordion =
               NON_ACCORDION_CATEGORY.includes(sectionName);
            if (isAccordion && !shouldBypassAccordion) {
               return (
                  <Drawer
                     key={`drawer-${index}`}
                     id={`drawer-${index}`}
                     className='accordion-border'
                     title={sectionName}
                     headingLevel='h3'
                  >
                     <div className='fields-container'>
                        {renderFields(fields, sectionKey, sectionForm)}
                     </div>
                  </Drawer>
               );
            } else {
               return (
                  <React.Fragment key={`fields-container-${sectionKey}`}>
                     <h4 className='section-name'>{sectionName}</h4>
                     <div className='fields-container'>
                        {renderFields(fields, sectionKey, sectionForm)}
                     </div>
                  </React.Fragment>
               );
            }
         }
      );
   };

   const searchCriteriaContent = () =>
      searchCriteriaConfig.map(({ screen, config }, screenIndex) =>
         ACCORDION_SCREENS.includes(screen) ? (
            <div
               key={`container-${screenIndex}`}
               className='spark-pad-b-1 spark-pad-0 spark-pad-0 last-accordion-bottom-border'
            >
               <Accordion>{RenderAccordion(config, true)}</Accordion>
            </div>
         ) : (
            RenderAccordion(config, false)
         )
      );

   return (
      <div
         className={`search-criteria-container ${
            isMinimized ? 'panel-minimized' : 'panel-expanded'
         }`}
      >
         {isMinimized ? (
            <div className='toggle-button-container'>
               <ToggleButton />
            </div>
         ) : (
            <form onSubmit={handleSubmit}>
               <div className='header'>
                  <h1 className='header-title'>SEARCH CRITERIA</h1>
                  <ToggleButton />
               </div>

               <div className='content-container'>
                  {searchCriteriaContent()}
               </div>

               <div className='button-container'>
                  <Button
                     size={SIZE.EXTRA_SMALL}
                     secondary
                     onClick={() =>
                        dispatchFormData({
                           type: FORM_DATA_ACTION_TYPE.RESET_FORM,
                        })
                     }
                     type='reset'
                     className='w-16'
                  >
                     CLEAR
                  </Button>
                  <Button
                     size={SIZE.EXTRA_SMALL}
                     secondary
                     onClick={onStopSearchRequest}
                     type='button'
                     className='w-16'
                  >
                     STOP
                  </Button>
                  <Button
                     size={SIZE.EXTRA_SMALL}
                     onClick={(event) => handleSubmit(event)}
                     type='submit'
                     className='w-16'
                  >
                     SEARCH
                  </Button>
               </div>
            </form>
         )}
      </div>
   );
};

SearchCriteriaComponent.propTypes = {
   searchCriteriaData: object.isRequired,
   onSubmit: func,
   onError: func,
   onSaveSearchCriteria: func,
   onStopSearchRequest: func,
   selectedSearchCriteria: object,
   isExistingCriteria: bool,
};

export default SearchCriteriaComponent;
